/**
 * Platform Console API contract — typed 1:1 against the agreed wire shape.
 *
 * Base path: ``/platform/console/v1`` — session-cookie auth (the ApiClient
 * already sends ``credentials: "include"``).
 *
 * NOTE: the backend for this area ships in the NEXT round. Shapes marked
 * ``TODO(console-contract)`` are reasonable projections of loosely-specified
 * parts of the contract (job/document/sync-history row shapes) — align them
 * with the Pydantic models when the BE lands.
 */

import type { Flow, FlowGraph, FlowTemplate, PendingApproval } from "@ando/core";

// ── Shared primitives ─────────────────────────────────────────────────────

/** Error envelope: every non-2xx body is ``{"error":{"code","message"}}``. */
export interface ConsoleErrorEnvelope {
  error: {
    code: string;
    message: string;
  };
}

/**
 * TODO(console-contract): job status vocabulary assumed from the ingestion
 * pipeline — confirm against the BE enum.
 */
export type ConsoleJobStatus = "queued" | "running" | "completed" | "failed";

// ── GET /context (caller identity + in-console capability tier) ─────────────
//
// The FE session ``UserInfo`` carries only the GLOBAL role (which surfaces a
// session may enter — see ``surfaceAccess``), not the caller's ORG role. This
// endpoint resolves the second, orthogonal dimension: the org role decides the
// in-console CAPABILITY tier. ``can_manage`` (org_role ∈ {owner, admin}) is the
// "Gestore" gate for the Account group (Membri + Fatturazione); a "Sviluppatore"
// (member) gets Build/Develop/Observe only. Ando staff resolve to org_role
// "owner" (``can_manage: true``).

/** ``GET /context`` — the caller's tenant + org role + management capability. */
export interface ConsoleContextInfo {
  tenant_id: string;
  org_role: string;
  can_manage: boolean;
}

// ── Embedded personal connectors ────────────────────────────────────────

export interface ConsoleEmbeddedIssuer {
  issuer: string;
  audience: string;
  jwks_uri: string;
  enabled: boolean;
}

export interface ConsoleEmbeddedSubject {
  issuer: string;
  subject: string;
  user_id: string;
  status: "active" | "revoked";
}

export interface ConsoleEmbeddedReturnUrl {
  issuer: string;
  url: string;
  active: boolean;
}

export interface ConsoleEmbeddedIdentity {
  issuers: ConsoleEmbeddedIssuer[];
  subjects: ConsoleEmbeddedSubject[];
  return_urls: ConsoleEmbeddedReturnUrl[];
}

export interface ConsoleEmbeddedCoverage {
  issuers: Array<{
    issuer: string;
    enabled: boolean;
    active_subjects: number;
    connected_users: number;
    active_connections: number;
    needs_reauth: number;
  }>;
}

export interface ConsoleDeveloperApp {
  id: string;
  name: string;
  status: string;
  identity_mode?: "server" | "oidc" | "managed";
  identity_issuer?: string | null;
  identity_audience?: string | null;
  login_redirect_uri?: string | null;
  managed_status?: "pending" | "ready" | "failed" | null;
  managed_audience_scope?: string | null;
  managed_org_scope?: string | null;
  created_at: string;
  return_urls: Array<{ url: string; active: boolean }>;
}

export interface ConsoleDeveloperApps {
  apps: ConsoleDeveloperApp[];
  managed_identity_enabled?: boolean;
  managed_audience_scope?: string | null;
  managed_org_scope?: string | null;
}

export interface ConsoleDeveloperAppCoverage {
  active_users: number;
  active_connections: number;
  needs_reauth: number;
  sync_errors: number;
}

export interface ConsoleDeveloperAppUser {
  id: string;
  subject: string;
  status: string;
  last_seen_at: string | null;
  active_connections: number;
  needs_reauth: number;
  sync_errors: number;
}

export interface ConsoleDeveloperAppUsers {
  users: ConsoleDeveloperAppUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConsoleManagedUserConnection {
  connection_id: string;
  provider: string;
  account_label: string | null;
  token_status: string;
  expires_at: string | null;
}

export interface ConsoleManagedUserConnections {
  connections: ConsoleManagedUserConnection[];
}

export interface ConsoleDeveloperTestSession {
  session_id: string;
  authorization_url: string;
  expires_at: string;
}

export interface ConsoleDeveloperTestConnection {
  connection_id: string;
  provider: string;
  account_label: string;
  token_status: string;
  grant_status: string;
  expires_at: string;
}

export interface ConsoleDeveloperTestConnections {
  connections: ConsoleDeveloperTestConnection[];
}

export type ConsolePersonalProvider =
  | "gmail"
  | "outlook"
  | "google_drive"
  | "onedrive"
  | "teams"
  | "google_chat"
  | "google_calendar"
  | "outlook_calendar";
export interface ConsolePersonalConnection {
  connection_id: string;
  provider: ConsolePersonalProvider;
  account_label: string | null;
  token_status: string;
  grant_status: string;
  expires_at: string | null;
  /** Verified legacy row for this exact tenant, owner, provider and account. */
  legacy_connection_ids?: string[];
  /** Workspace owns sync only after the server has verified the migration is ready. */
  sync_owner?: "workspace" | "engine";
  auto_sync?: {
    enabled: boolean;
    status: string;
    last_success_at: string | null;
    next_due_at: string | null;
    last_error: string | null;
  };
  legacy_sync?: {
    enabled: boolean;
    status: string | null;
    last_success_at: string | null;
    last_error: string | null;
  } | null;
}
export interface ConsolePersonalConnections {
  connections: ConsolePersonalConnection[];
}
export interface ConsolePersonalSession {
  session_id: string;
  authorization_url: string;
  expires_at: string;
}
export interface ConsolePersonalTest {
  status: string;
  provider: ConsolePersonalProvider;
  account_label: string | null;
}
export interface ConsolePersonalPreview {
  provider: ConsolePersonalProvider;
  items: Array<{ id: string; title: string; subtitle: string | null }>;
}
export interface ConsolePersonalSyncResult {
  provider: ConsolePersonalProvider;
  connection_id: string;
  processed: number;
  skipped: number;
  next_page_token: string | null;
}
export type ConsolePersonalEmailRange = "7d" | "30d" | "90d" | "custom";
export interface ConsolePersonalSyncSettings {
  time_range: ConsolePersonalEmailRange;
  after_date: string | null;
  before_date: string | null;
}
export interface ConsolePersonalSyncPreview {
  matching_items: number | null;
  estimated_seconds: null;
  estimated_storage_mb: null;
  status: "ready" | "unavailable";
  unit: "email" | "messages";
  approximate: boolean;
}
export interface ConsolePersonalDriveItem {
  id: string;
  name: string;
  path?: string;
  kind: "folder" | "file";
  mime_type?: string | null;
  size_bytes?: number | null;
}
export interface ConsolePersonalDriveItems {
  items: ConsolePersonalDriveItem[];
  next_page_token: string | null;
}
export interface ConsolePersonalDriveSelection {
  folder_ids: string[];
  file_ids: string[];
  selected_items?: Array<{
    id: string;
    name: string | null;
    kind: "folder" | "file";
    path: string | null;
  }>;
}
export interface ConsolePersonalChatItem {
  id: string;
  name: string;
  kind: "chat";
}
export interface ConsolePersonalChats {
  items: ConsolePersonalChatItem[];
  next_page_token: string | null;
}
export interface ConsolePersonalChatSelection {
  chat_ids: string[];
}
export interface ConsolePersonalCalendarItem {
  id: string;
  name: string;
  is_primary: boolean;
}
export interface ConsolePersonalCalendars {
  provider: ConsolePersonalProvider;
  items: ConsolePersonalCalendarItem[];
}
export interface ConsolePersonalCalendarSelection {
  calendar_ids: string[];
  past_days: number;
  future_days: number;
}
export interface ConsolePersonalSyncStatus {
  status: string;
  scan_active: boolean;
  indexed_documents: number;
  indexed_emails?: number | null;
  indexed_attachments?: number | null;
  last_success_at: string | null;
  next_due_at: string | null;
  last_error: string | null;
}
export interface ConsolePersonalQueryResult {
  k: number;
  count: number;
  results: Array<{
    document_id?: string;
    chunk_id?: string;
    title?: string;
    section?: string | null;
    text?: string;
    source?: string;
    score?: number;
  }>;
  answer?: string | null;
  citations?: Array<{ document_id: string; chunk_id: string; quote?: string | null }>;
  no_results?: boolean;
}

// ── GET /overview ─────────────────────────────────────────────────────────

export interface ConsoleRecentJob {
  id: string;
  /**
   * Kept as ``string``: the real BE vocabulary drifts from the FE union —
   * badges resolve it through safe accessors (``StatusBadges``).
   */
  status: string;
  document: string;
  created_at: string;
}

export interface ConsoleLastSyncEntry {
  type: string;
  at: string;
  /** See ``ConsoleRecentJob.status`` — loosely typed on purpose. */
  status: string;
  /** New per-connection overview rows keep personal and legacy sync distinct. */
  kind?: "personal" | "workspace";
  connection_id?: string | null;
  label?: string | null;
}

/** Optional per-source chunk counter (incoming BE contract). */
export interface ConsoleSourceBreakdownEntry {
  source: string;
  chunks: number;
}

/**
 * UI projection of ``GET /overview``. The real wire shape differs (nested
 * ``documents`` object, ``job_id``/``filename``, ``provider``/``last_sync_at``)
 * — ``api/normalize.ts`` maps every known variant into THIS shape, so the
 * pages depend on one stable model.
 */
export interface ConsoleOverview {
  documents: number;
  chunks: number;
  storage_bytes: number;
  connectors_active: number;
  recent_jobs: ConsoleRecentJob[];
  last_sync_by_connector: ConsoleLastSyncEntry[];
  sources_breakdown?: ConsoleSourceBreakdownEntry[];
}

// ── Keys ──────────────────────────────────────────────────────────────────

/**
 * Optional per-key resource restriction. ``null`` = full access.
 *
 * ``action_connectors`` is the WRITE dimension (the write-loop): the custom
 * connector SLUGS this key may execute actions on. Omit for no write
 * restriction. Orthogonal to the read allow-lists (``sources`` /
 * ``connector_ids``) — a key can keep full reads yet only write to a subset.
 *
 * ``monthly_spend_limit_cents`` is the SPEND dimension (not a resource
 * allow-list): a per-key MONTHLY spend cap in USD cents. Omit for an
 * uncapped key; the runtime enforces it and returns ``X-Spend-*`` headers +
 * a ``402 spend_limit_exceeded`` once the cap is reached. Mirrors the BE
 * ``VALID_POLICY_KEYS`` (``ando-agent`` ``platform_api_key_service``).
 */
export interface ConsoleResourcePolicy {
  sources?: string[];
  connector_ids?: string[];
  action_connectors?: string[];
  monthly_spend_limit_cents?: number;
}

/**
 * Scope catalog entry — ``GET /keys/scopes`` returns
 * ``{"scopes":[{id,label,description}]}``. The ``id`` is the REAL wire
 * scope (e.g. ``query:read``) that ``POST /keys`` accepts; ``label`` and
 * ``description`` are the human-readable copy the wizard renders.
 */
export interface ConsoleScopeInfo {
  id: string;
  label: string;
  description: string;
}

export interface ConsoleScopesResponse {
  scopes: ConsoleScopeInfo[];
}

export interface ConsoleApiKey {
  key_id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  resource_policy: ConsoleResourcePolicy | null;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  principal_mode?: "service" | "end_user_only" | "app_user_server" | "app_user_oidc";
  end_user_issuer?: string | null;
  app_id?: string | null;
}

export interface ConsoleKeysResponse {
  keys: ConsoleApiKey[];
}

export interface ConsoleCreateKeyRequest {
  name: string;
  scopes: string[];
  resource_policy?: ConsoleResourcePolicy | null;
  expires_at?: string | null;
  live?: boolean;
  principal_mode?: "service" | "end_user_only" | "app_user_server" | "app_user_oidc";
  end_user_issuer?: string | null;
  app_id?: string | null;
}

/** ``api_key`` is the FULL secret — returned exactly once, never again. */
export interface ConsoleCreateKeyResponse {
  api_key: string;
  key_id: string;
  key_prefix: string;
  scopes: string[];
  principal_mode?: "service" | "end_user_only" | "app_user_server" | "app_user_oidc";
  end_user_issuer?: string | null;
  app_id?: string | null;
}

export interface ConsoleKeyAuditEvent {
  event: string;
  detail: string;
  created_at: string;
}

export interface ConsoleKeyAuditResponse {
  events: ConsoleKeyAuditEvent[];
}

// ── Connectors ────────────────────────────────────────────────────────────
//
// Typed 1:1 against the enriched console bridge
// (``src/api/platform/console/connectors.py``). Every entry is labelled by
// WHITE-LABEL capability id (``crm`` / ``drive`` / ``database`` …) — the
// vendor slug is an internal routing detail that only ever rides inside
// ``vendors`` / ``providers`` and is NEVER shown to the user.

/** Capability-level status (the catalog card). */
export type ConsoleConnectorStatus = "connected" | "not_connected" | "error";

/** Per-connection status (a single account / database of a capability). */
export type ConsoleConnectionStatus = "connected" | "error" | "syncing";

/** Which fleet a capability is served by. */
export type ConsoleConnectorSurface = "saas" | "structured" | "mixed";

/** ``{active, total}`` connection counters, keyed by internal provider slug. */
export interface ConsoleProviderStat {
  active: number;
  total: number;
}

/**
 * A per-connection row the CATALOG carries for a capability (the structured
 * surface populates these; the deep-dive detail is the authoritative
 * per-connection source). Used as the deep-dive ENTRY POINT: the card opens
 * ``/connectors/{connection_id}`` with the first instance's id.
 */
export interface ConsoleConnectorInstance {
  connection_id: string;
  /** Display-safe name (db name / account label) — never a token. */
  name: string;
  /** Internal vendor slug — routing detail, not shown. */
  provider: string;
  status: ConsoleConnectionStatus;
  /** Discovered object/schema count. */
  schemas: number;
  last_sync_at: string | null;
  created_at?: string | null;
  last_used_at?: string | null;
}

/** GET /connectors entry — one capability with the org's status for it. */
export interface ConsoleConnector {
  /** White-label capability id — NEVER a vendor. */
  type: string;
  connection_type: string | null;
  surface: ConsoleConnectorSurface;
  /** Selectable vendor slugs (internal routing detail — never rendered). */
  vendors: string[];
  status: ConsoleConnectorStatus;
  /** Total connections of this capability across the org. */
  connections: number;
  active_connections: number;
  providers: Record<string, ConsoleProviderStat>;
  last_sync_at: string | null;
  /** Per-connection rows (structured surface); [] for a bare SaaS entry. */
  instances: ConsoleConnectorInstance[];

  // ── Legacy compat (Keys surface) ────────────────────────────────────────
  // The pre-enrichment catalog carried these at the top level. The real
  // enriched catalog no longer does; kept OPTIONAL so the API-key
  // resource-scoping picker keeps compiling until it migrates to
  // ``instances``. The connectors UI itself reads the fields above.
  label?: string;
  connection_id?: string;
  items_count?: number;
}

export interface ConsoleConnectorsResponse {
  connectors: ConsoleConnector[];
  count: number;
  /** Owner-bound connections visible only to the signed-in Console member. */
  personal_connections: ConsolePersonalConnection[];
  /** True when the structured surface read failed and the catalog degraded. */
  degraded: boolean;
}

/** Where a broken/new connection is (re-)authorized — the product's own decision. */
export interface ConsoleReconnectEntrypoint {
  redirect_url: string;
  /** ``oauth_redirect`` | ``credentials_form`` | ``unified_start``. */
  flow: string;
  vendor: string | null;
}

/** Console-relative disconnect entrypoint of one connection. */
export interface ConsoleDisconnectAction {
  method: string;
  path: string;
}

/** Real ``sync_state`` of one connection (email/chat/drive); null when none. */
export interface ConsoleConnectionSync {
  /** ``to_display_status`` mapping (done/syncing/pending/error …). */
  sync_status: string;
  sync_error: string | null;
  last_full_sync_at: string | null;
  last_incremental_sync_at: string | null;
  last_sync_at: string | null;
  items_synced: number | null;
  total_chunks: number | null;
}

/** One discovered object of a structured connection (name + row count). */
export interface ConsoleSchemaObject {
  name: string;
  record_count: number | null;
}

/** Discovered schema of a structured connection; null for SaaS. */
export interface ConsoleConnectionSchemas {
  object_count: number | null;
  record_count: number | null;
  last_discovered_at: string | null;
  /** Name-ordered SAMPLE (capped BE-side); count is authoritative. */
  objects: ConsoleSchemaObject[];
}

/** Normalized counters shared by both surfaces. */
export interface ConsoleConnectionCounts {
  rows: number | null;
  chunks: number | null;
  objects: number | null;
  items: number | null;
}

/** Headline usage figure for a connection. */
export interface ConsoleConnectionUsage {
  figure: number;
  /** ``items`` (SaaS) | ``records`` (structured). */
  kind: string;
}

/** One connection of a capability, fully enriched for the deep-dive. */
export interface ConsoleConnection {
  connection_id: string;
  /** Internal vendor slug — routing detail, not shown. */
  provider: string;
  /** Account identifier / db name — display-safe. */
  label: string | null;
  connection_identifier: string | null;
  /** Normalized token status (``active`` | ``needs_reauth``). */
  token_status: string;
  status: ConsoleConnectionStatus;
  needs_reauth: boolean;
  scope: string | null;
  expires_at: string | null;
  created_at: string | null;
  last_used_at: string | null;
  last_sync_at: string | null;
  sync: ConsoleConnectionSync | null;
  schemas: ConsoleConnectionSchemas | null;
  counts: ConsoleConnectionCounts;
  usage: ConsoleConnectionUsage | null;
  reconnect: ConsoleReconnectEntrypoint;
  disconnect: ConsoleDisconnectAction;
}

/** Capability-level rollup that heads the deep-dive response. */
export interface ConsoleCapability {
  type: string;
  /** == type (the FE maps it to a human, white-label label). */
  label: string;
  connection_type: string | null;
  surface: ConsoleConnectorSurface;
  vendors: string[];
  status: ConsoleConnectorStatus;
  connections_count: number;
  active_connections: number;
  providers: Record<string, ConsoleProviderStat>;
  last_sync_at: string | null;
  needs_reauth: boolean;
  reconnect: ConsoleReconnectEntrypoint;
}

/** GET /connectors/{connection_id} — the capability deep-dive. */
export interface ConsoleConnectorDetail {
  capability: ConsoleCapability;
  focused_connection_id: string;
  /** EVERY connection of the capability — a capability with N never collapses. */
  connections: ConsoleConnection[];
  degraded: boolean;
}

export interface ConsoleConnectResponse {
  redirect_url: string;
  type?: string;
  vendor?: string | null;
  flow?: string;
}

// ── Custom connectors (developer Push-API) ────────────────────────────────
//
// A developer's OWN Push-API connector, registered against their project.
// Unlike the white-label vendor catalog above, the developer picks the slug
// and name and pushes records from their app (open a sync → push batches →
// complete) with a platform AppKey scoped ``sources:manage`` + ``ingest:write``.
// Typed 1:1 against the backend ``ConnectorSummary`` wire shape.
//
//   POST   /custom-connectors            register a connector → ConnectorSummary
//   GET    /custom-connectors            { connectors, count } (newest first)
//   GET    /custom-connectors/{slug}     ConnectorSummary | 404
//   DELETE /custom-connectors/{slug}     disable (keeps indexed content queryable)

/** ``kind`` — connector family. ``push`` and ``manifest`` are registrable
 * today; ``code`` is announced but not yet available (400 on create). */
export type ConsoleCustomConnectorKind = "push" | "manifest" | "code";

/** ``ConnectorSummary`` — one registered custom connector. */
export interface ConsoleCustomConnector {
  /** Stable id every runtime sync call addresses; unique in the project. */
  slug: string;
  name: string;
  /** Connector family — ``push`` today (kept as string; the wire may add more). */
  kind: string;
  /** False once disabled — indexed content stays queryable, new syncs stop. */
  active: boolean;
  /** Free-form connector metadata — NEVER credentials. */
  config: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  last_sync_at: string | null;
  record_count: number | null;
  /** AppKey identity used by scheduled manifest syncs. Null means unbound. */
  manifest_principal_key_id?: string | null;
}

export interface ConsoleCustomConnectorsResponse {
  connectors: ConsoleCustomConnector[];
  count: number;
}

// ── Record contract + kinds (self-describing) ────────────────────────────────

/** One field of the Record contract; extra keys carry per-field extras
 * (max_chars / enum / default / max_keys / body). */
export interface ConsoleRecordFieldSpec {
  name: string;
  type: string;
  required: boolean;
  description: string;
  [extra: string]: unknown;
}

/** The shape a Push connector emits — fields, rules and enforced limits. */
export interface ConsoleRecordContract {
  fields: ConsoleRecordFieldSpec[];
  record_kinds: string[];
  reserved_metadata_prefix: string;
  reserved_metadata_keys: string[];
  max_records_per_batch: number;
  max_metadata_keys: number;
  max_upload_bytes: number;
  supported_file_formats: string;
  rules: string[];
}

/** One rung of the connector ladder (push available; manifest/code preview). */
export interface ConsoleConnectorKind {
  kind: string;
  available: boolean;
  title: string;
  summary: string;
  runs: string;
  credentials: string;
  required_scopes: string[];
  preview?: boolean;
}

/** ``GET /custom-connectors/kinds`` — kinds + the Record contract. */
export interface ConsoleConnectorKindsResponse {
  kinds: ConsoleConnectorKind[];
  record_contract: ConsoleRecordContract;
}

// ── Dry-run (validate records against the contract) ──────────────────────────

export interface ConsoleRecordRejection {
  index: number;
  field: string;
  reason: string;
}

/** What one accepted record WOULD index — no bytes fetched, no writes. */
export interface ConsoleRecordPreview {
  index: number;
  external_id: string;
  kind: string;
  body: string;
  content_type: string | null;
  metadata_keys: string[];
  acl: string;
  content_hash: string;
  [extra: string]: unknown;
}

/** ``POST /custom-connectors/{slug}/validate`` — 200 even with rejections;
 * ``accepted + rejected.length`` equals the records sent. */
export interface ConsoleRecordValidateResponse {
  accepted: number;
  rejected: ConsoleRecordRejection[];
  records: ConsoleRecordPreview[];
}

// ── Sync-run history (observability) ─────────────────────────────────────────

/** One run in the sync history. */
export interface ConsoleSyncSummary {
  sync_id: string;
  mode: string | null;
  status: string;
  indexed: number;
  deleted: number;
  unchanged: number;
  rejected: number;
  duration_ms: number | null;
  started_at: string | null;
  completed_at: string | null;
}

/** ``GET /custom-connectors/{slug}/syncs`` — newest run first. */
export interface ConsoleSyncListResponse {
  connector: string;
  syncs: ConsoleSyncSummary[];
  count: number;
}

/** ``POST /custom-connectors`` body. */
export interface ConsoleCustomConnectorCreate {
  slug: string;
  name: string;
  kind: string;
  /** Free-form metadata object — NEVER credentials; omit when empty. */
  config?: Record<string, unknown> | null;
}

/** ``DELETE /custom-connectors/{slug}`` — disable, not destroy. */
export interface ConsoleCustomConnectorDisableResponse {
  disabled: boolean;
  slug: string;
}

/**
 * ``PATCH /custom-connectors/{slug}`` body — every field optional.
 *
 *   - ``name``     → rename;
 *   - ``enabled``  → ``true`` re-enables a disabled connector (syncs resume,
 *     the ledger survived so incremental picks up), ``false`` disables it
 *     (same semantics as DELETE);
 *   - ``config``   → FULL replacement (no deep merge). Manifest connectors:
 *     re-validated server-side; a fresh ``manifest.auth.secret`` ROTATES the
 *     sealed credential, omitting it keeps the current one; the cursor state
 *     is reset (next run is a clean full sync).
 */
export interface ConsoleCustomConnectorUpdate {
  name?: string;
  enabled?: boolean;
  config?: Record<string, unknown>;
}

// ── Custom-connector ACTIONS (developer write-loop) ─────────────────────────
//
// The write-side counterpart to the reads above. A developer DECLARES the
// actions their own app supports (name + params JSON-Schema + risk); when Ando
// executes one it dispatches a signed request to the developer's webhook and
// the app performs it. Two families:
//
//   Action DEFINITIONS — the catalog declared on a connector:
//     POST   /custom-connectors/{slug}/actions          declare       → ActionDefinition
//     GET    /custom-connectors/{slug}/actions           { actions, count }
//     DELETE /custom-connectors/{slug}/actions/{name}    { deleted, connector, name }
//
//   Action REQUESTS — one propose→execute lifecycle (test/run from the console):
//     POST   /actions                 propose  → ActionRequest (status "proposed", carries preview)
//     POST   /actions/{id}/execute    dispatch → ActionRequest ("dispatched" | "failed")
//     GET    /actions/{id}            ActionRequest
//     GET    /actions?connector={slug}&limit={n}   { actions, count }

/** ``ActionDefinition`` — one action a custom connector declares it can perform. */
export interface ConsoleActionDefinition {
  /** Connector slug this action is declared on. */
  connector: string;
  /** Lowercase name (``a-z0-9._-``, max 64), unique per connector. */
  name: string;
  description: string | null;
  /** JSON Schema (Draft 2020-12) the params are validated against; ``{}`` = unconstrained. */
  params_schema: Record<string, unknown>;
  /** ``low`` (default) | ``medium`` | ``high`` | ``critical`` — high/critical need confirm. */
  risk: string;
  /**
   * Optional HTTP binding — how Ando calls the developer's system for this
   * capability (``{method, path, body_template?, headers?, timeout_seconds?}``).
   * ``null``/absent = webhook-only dispatch (the propose→execute loop).
   */
  binding?: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ConsoleActionDefinitionsResponse {
  actions: ConsoleActionDefinition[];
  count: number;
}

/** ``POST /custom-connectors/{slug}/actions`` body. */
export interface ConsoleActionDefinitionCreate {
  name: string;
  description?: string;
  /** Omit to leave the params unconstrained. */
  params_schema?: Record<string, unknown>;
  /** Defaults to ``low`` server-side. */
  risk?: string;
  /**
   * Optional HTTP binding (``{method, path, body_template?, headers?,
   * timeout_seconds?}``). Omit for webhook-only dispatch; re-declaring
   * without it clears it server-side (full replacement).
   */
  binding?: Record<string, unknown>;
}

/** ``DELETE /custom-connectors/{slug}/actions/{name}``. */
export interface ConsoleActionDefinitionDeleteResponse {
  deleted: boolean;
  connector: string;
  name: string;
}

/** Lifecycle of a single action request. */
export type ConsoleActionRequestStatus =
  | "proposed"
  | "dispatched"
  | "completed"
  | "failed"
  | "canceled";

/** ``ActionRequest`` — one propose→execute lifecycle of an action. */
export interface ConsoleActionRequest {
  id: string;
  connector: string;
  action: string;
  params: Record<string, unknown>;
  status: ConsoleActionRequestStatus;
  risk: string;
  /** Read-only projection of what will run — shown before Confirm; null once dispatched. */
  preview: Record<string, unknown> | null;
  /** The developer app's completion payload — null until it reports back. */
  result: Record<string, unknown> | null;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
  dispatched_at: string | null;
  completed_at: string | null;
}

export interface ConsoleActionRequestsResponse {
  actions: ConsoleActionRequest[];
  count: number;
  /** Cursor pagination (newest first): pass ``next_cursor`` back as ``cursor``
   * until ``has_more`` is false. */
  has_more?: boolean;
  next_cursor?: string | null;
}

/** ``POST /actions`` body — propose an action request (returns it "proposed"). */
export interface ConsoleActionRequestCreate {
  connector: string;
  action: string;
  /** Validated against the action's ``params_schema``; omit for none. */
  params?: Record<string, unknown>;
}

/** ``POST /actions/{id}/execute`` body — ``confirm_risk`` required for high/critical. */
export interface ConsoleActionExecuteRequest {
  confirm_risk?: boolean;
}

/** ``POST /custom-connectors/{slug}/actions/{name}/invoke`` body — synchronous
 * HTTP-bound capability run; ``confirm_risk`` required for high/critical. */
export interface ConsoleActionInvokeRequest {
  /** Validated against the action's ``params_schema``; omit for none. */
  params?: Record<string, unknown>;
  confirm_risk?: boolean;
}

/** Synchronous outcome of an HTTP-bound capability invocation. */
export interface ConsoleActionInvokeResponse {
  ok: boolean;
  /** HTTP status returned by the bound endpoint. */
  status_code?: number | null;
  /** Parsed response payload from the bound endpoint. */
  response?: Record<string, unknown> | null;
  /** True when a TEST key accepted+validated but never called the endpoint. */
  test?: boolean | null;
}

// ── Webhooks (console session projection) ─────────────────────────────────
//
// Session projection of the v1 webhook surface
// (``src/api/platform/console/webhooks.py``) — register/list/test/delete the
// HTTPS endpoint that receives signed event deliveries (Standard Webhooks),
// including the one that closes the actions write-loop (``action.requested``).
//
//   POST   /webhooks               → WebhookCreated (secret EXACTLY ONCE)
//   GET    /webhooks               → { webhooks, count }
//   DELETE /webhooks/{id}          → { deleted, webhook_id }
//   POST   /webhooks/{id}/test     → synchronous test.ping outcome

/** The events a project may subscribe to (mirrors ``VALID_WEBHOOK_EVENTS``). */
export const CONSOLE_WEBHOOK_EVENTS = [
  "ingestion.completed",
  "source.sync.completed",
  "usage.threshold_reached",
  "action.requested",
] as const;
export type ConsoleWebhookEvent = (typeof CONSOLE_WEBHOOK_EVENTS)[number];

/** One endpoint in the ``GET /webhooks`` listing — never any secret. */
export interface ConsoleWebhookSummary {
  webhook_id: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string | null;
}

/** ``POST /webhooks`` body — ``url`` must be HTTPS, ``events`` a non-empty
 * subset of the documented event names. */
export interface ConsoleWebhookCreate {
  url: string;
  events: string[];
}

/** 201 body — ``secret`` (``whsec_`` prefix) is shown EXACTLY ONCE here. */
export interface ConsoleWebhookCreated extends ConsoleWebhookSummary {
  secret: string;
}

export interface ConsoleWebhooksResponse {
  webhooks: ConsoleWebhookSummary[];
  count: number;
}

export interface ConsoleWebhookDeleted {
  deleted: boolean;
  webhook_id: string;
}

/** ``POST /webhooks/{id}/test`` — a failing receiver is reported in the 200
 * body (``delivered:false`` + ``error``), not as an API error. */
export interface ConsoleWebhookTestResult {
  webhook_id: string;
  event: string;
  delivered: boolean;
  http_status: number | null;
  error: string | null;
}

// Delivery history + replay (Aug 2026):
//
//   GET  /webhooks/{id}/deliveries        → { deliveries, count } (newest first)
//   POST /webhooks/deliveries/{id}/replay → re-queued, immediate retry, FRESH signature

/** Lifecycle of one delivery's attempt chain. */
export type ConsoleWebhookDeliveryStatus = "pending" | "delivered" | "failed";

/** One delivery in ``GET /webhooks/{id}/deliveries`` — newest first. */
export interface ConsoleWebhookDelivery {
  delivery_id: string;
  event: string;
  status: ConsoleWebhookDeliveryStatus;
  attempts: number;
  /** The signed event body the receiver got (or will get). */
  payload: Record<string, unknown>;
  next_attempt_at?: string | null;
  created_at?: string | null;
  delivered_at?: string | null;
}

export interface ConsoleWebhookDeliveriesResponse {
  deliveries: ConsoleWebhookDelivery[];
  count: number;
}

/** ``POST /webhooks/deliveries/{id}/replay`` — back in the queue with an
 * immediate retry and a fresh signature (works on delivered ones too). */
export interface ConsoleWebhookDeliveryReplayed {
  delivery_id: string;
  event: string;
  status: "pending";
  attempts: number;
}

// ── Documents / jobs ──────────────────────────────────────────────────────

/** TODO(console-contract): document row shape assumed for the paginated list. */
export interface ConsoleDocument {
  id: string;
  name: string;
  source: string;
  chunks: number;
  size_bytes: number;
  status: ConsoleJobStatus;
  created_at: string;
  owner_user_id?: string | null;
  can_manage_access?: boolean;
  access_visibility?: "private" | "restricted" | "project" | "legacy";
  access_grant_count?: number;
}

/** Access to data is separate from the ability to administer an app or source. */
export interface ConsoleResourceGrant {
  principal_kind: "member" | "app_user";
  principal_id: string;
  app_id?: string | null;
  principal_label?: string | null;
  principal_active?: boolean;
  /** null = entire structured connection; nonempty physical names = exact table scope. */
  table_allowlist?: string[] | null;
}

export interface ConsoleResourceAccess {
  resource_kind: "document" | "connection";
  resource_id: string;
  resource_label?: string | null;
  /** Provider/capability for connection rows; never contains credentials. */
  resource_provider?: string | null;
  owner_user_id: string;
  visibility: "private" | "project" | "restricted" | "legacy";
  grants: ConsoleResourceGrant[];
  can_manage: boolean;
  /** Server capability for renaming this connection in Console. */
  can_rename?: boolean;
  /** Present only when sharing this connection is enforced end to end. */
  allowed_principal_kinds?: Array<"member" | "app_user">;
  /** Non-secret selected provider scope; omitted for documents and structured connections. */
  share_scope?: {
    provider: string;
    selected_items: Record<string, string[]>;
    selected_count: number;
    shareable: boolean;
    sync_selection?: Record<string, string[]>;
  };
  /** Advertised only for structured sources whose table-scoped grants are enforced. */
  available_tables?: string[];
}

/** Owner-scoped inventory; grants are active and effective at read time. */
export interface ConsoleAccessOverviewPage {
  items: ConsoleResourceAccess[];
  total: number;
  limit: number;
  offset: number;
}

/** A content item already present in Ando knowledge, independent of provider. */
export interface ConsoleIndexedKnowledgeItem {
  item_id: string;
  title: string;
  source: string;
  subtitle: string | null;
  ingested_at: string | null;
  selected?: boolean;
}

export interface ConsoleIndexedKnowledgeItemsPage {
  items: ConsoleIndexedKnowledgeItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConsoleSelectedKnowledgePage {
  items: { item_id: string }[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConsoleDocumentsPage {
  documents: ConsoleDocument[];
  page: number;
  page_size: number;
  total: number;
}

export interface ConsoleDocumentContent {
  id: string;
  name: string;
  source: string;
  content_type: string | null;
  text?: string | null;
  html?: string | null;
  download_url?: string | null;
  preview_url?: string | null;
  attachments?: Array<{ name: string; content_type?: string; url: string }>;
  from?: string;
  to?: string[];
  cc?: string[];
  subject?: string;
  date?: string;
}

export interface ConsoleJobsResponse {
  jobs: ConsoleRecentJob[];
}

/**
 * TODO(console-contract): the Documents page needs upload / delete /
 * per-document job history. Projected endpoints (confirm when the BE
 * lands): ``POST /documents`` (multipart), ``DELETE /documents/{id}``,
 * ``GET /documents/{id}/jobs`` (returns ``ConsoleJobsResponse``).
 * Upload constraints mirrored client-side in ``lib/uploads.ts``:
 * PDF/DOCX/TXT/MD, max 25 MB.
 */

// ── GET /usage?window=30d ─────────────────────────────────────────────────

export const CONSOLE_USAGE_WINDOWS = ["7d", "30d", "90d"] as const;
export type ConsoleUsageWindow = (typeof CONSOLE_USAGE_WINDOWS)[number];

export const CONSOLE_USAGE_WINDOW_DAYS: Record<ConsoleUsageWindow, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export function isConsoleUsageWindow(value: unknown): value is ConsoleUsageWindow {
  return typeof value === "string" && (CONSOLE_USAGE_WINDOWS as readonly string[]).includes(value);
}

export interface ConsoleUsageDay {
  date: string;
  queries: number;
  /**
   * Marked-up estimated LLM cost for the day, in USD. This is the DEVELOPER
   * console's technical metering (the ``cost_usd`` the BE ``/usage`` already
   * returns, carrying the platform-admin markup %) — NOT the customer-facing
   * "% only" surface, which is ``/usage/breakdown`` (counts, never currency).
   * Optional so pre-cost clients/fixtures stay valid.
   */
  cost_usd?: number;
  pages_ingested: number;
  storage_bytes: number;
}

/** Window sums the BE ``/usage`` returns alongside ``days``. */
export interface ConsoleUsageTotals {
  queries: number;
  /** Marked-up estimated LLM spend over the window, in USD. */
  cost_usd: number;
  documents: number;
  pages_ingested: number;
  storage_bytes: number;
  /** All-time current stored bytes (not window-bound). */
  storage_bytes_current: number;
}

export interface ConsoleUsage {
  days: ConsoleUsageDay[];
  /** Window sums; optional so pre-totals clients/fixtures stay valid. */
  totals?: ConsoleUsageTotals;
}

// ── GET /usage/breakdown?window=30d ───────────────────────────────────────
//
// The tenant's OWN LLM consumption, as request/token COUNTS and relative %
// only — this surface NEVER shows a currency figure (product rule).

/** One day of the requests + tokens trend (zero-filled over the window). */
export interface ConsoleUsageTrendDay {
  date: string;
  requests: number;
  tokens: number;
}

/** A consuming model — the "top consumers" rows. */
export interface ConsoleUsageModelEntry {
  model: string;
  provider: string | null;
  requests: number;
  tokens: number;
}

/** Consumption grouped by call PURPOSE (source: agent_query, email_sync, …). */
export interface ConsoleUsageSourceEntry {
  source: string;
  requests: number;
  tokens: number;
}

/** Request + token counts for a window — used for the "vs previous" delta. */
export interface ConsoleUsageWindowTotals {
  requests: number;
  tokens: number;
}

export interface ConsoleUsageBreakdown {
  window: ConsoleUsageWindow;
  days: ConsoleUsageTrendDay[];
  totals: ConsoleUsageWindowTotals;
  /** The equal-length window immediately before ``days`` — the delta baseline. */
  previous: ConsoleUsageWindowTotals;
  /** Top consuming models, tokens-descending. */
  by_model: ConsoleUsageModelEntry[];
  /** Every call purpose, tokens-descending. */
  by_source: ConsoleUsageSourceEntry[];
}

// ── Models + BYOK ─────────────────────────────────────────────────────────
//
// The org's project-level model choice per PURPOSE plus its bring-your-own-key
// (BYOK) provider key. Typed 1:1 against the backend contract
// (``ando-agent/src/api/platform/console/models.py``):
//
//   GET    /models                  configs (effective, per purpose) + catalog
//   PUT    /models/{purpose}        choose provider + model
//   DELETE /models/{purpose}        revert to the platform default
//   POST   /models/{purpose}/byok   store / rotate the customer key
//   DELETE /models/{purpose}/byok   drop the customer key
//
// A response NEVER carries a provider key — only a mask (``********f4a1``) and
// the last 4 characters. The plaintext is write-only.

/** The four seams of the stack a model plugs into (backend ``PURPOSES``). */
export const CONSOLE_MODEL_PURPOSES = ["answer", "agentic", "rerank", "embedding"] as const;
export type ConsoleModelPurpose = (typeof CONSOLE_MODEL_PURPOSES)[number];

/** Which fallback rung answered: chosen by the project, or inherited. */
export type ConsoleModelSource = "tenant" | "platform_default" | "hard_default";

/** BYOK key state as the console reads it (never the plaintext). */
export type ConsoleByokState = "none" | "configured" | "unreadable";

/** One selectable model in a purpose's catalog. */
export interface ConsoleModelOption {
  provider: string;
  model: string;
  /**
   * Capability copy DERIVED from repo facts (``vertex-direct`` /
   * ``zero-data-retention`` / ``openrouter`` / ``local-runtime`` / ``jina``,
   * the cost band ``no-token-cost`` / ``economy`` / ``standard`` /
   * ``premium``, and ``byok``). Technical vocabulary — rendered verbatim.
   */
  labels: string[];
  input_usd_per_1m: number | null;
  output_usd_per_1m: number | null;
  is_platform_default: boolean;
  selectable?: boolean;
  byok_supported: boolean;
}

/** A purpose's EFFECTIVE configuration — display-safe, never a secret. */
export interface ConsoleModelConfig {
  purpose: string;
  /** Human label for the purpose (e.g. "Risposta finale"). */
  label: string;
  description: string;
  provider: string;
  model: string;
  params: Record<string, unknown>;
  source: ConsoleModelSource;
  byok_state: ConsoleByokState;
  byok_masked: string | null;
  byok_last4: string | null;
  byok_supported: boolean;
  updated_at: string | null;
  /** Explicit runtime coverage; absent on older servers means read-only. */
  runtime_enabled?: boolean;
  configurable?: boolean;
  provider_payer?: "ando" | "customer";
  credential_status?: "none" | "saved" | "verified" | "active" | "error";
  fallback_policy?: "strict";
  coverage?: string[];
  delegation_approved?: boolean;
  tariff?: {
    ready: boolean;
    markup_pct: number | null;
    reference_pricing_version: string | null;
    tariff_version: string | null;
  };
}

/** ``GET /models`` body — every purpose plus what each can run. */
export interface ConsoleModelsResponse {
  configs: ConsoleModelConfig[];
  /** Selectable models keyed by purpose. */
  catalog: Record<string, ConsoleModelOption[]>;
}

/** ``PUT /models/{purpose}`` body. */
export interface ConsoleSetModelRequest {
  provider: string;
  model: string;
  params?: Record<string, unknown> | null;
  runtime_enabled?: boolean;
  fallback_policy?: "strict";
}

/** Body of every write route: the resulting effective configuration. */
export interface ConsoleModelConfigResponse {
  config: ConsoleModelConfig;
}

/** ``DELETE /models/{purpose}`` — ``reverted`` = a choice actually existed. */
export interface ConsoleModelRevertResponse extends ConsoleModelConfigResponse {
  reverted: boolean;
}

/** ``DELETE /models/{purpose}/byok`` — ``removed`` = a key was actually stored. */
export interface ConsoleByokRemovedResponse extends ConsoleModelConfigResponse {
  removed: boolean;
}

// ── POST /query (playground) ──────────────────────────────────────────────

export type ConsoleQueryMode = "retrieve" | "answer";

export interface ConsoleQueryRequest {
  query: string;
  mode: ConsoleQueryMode;
  k: number;
  as_key_id?: string;
}

/** TODO(console-contract): "runtime-shaped" result row — align with the BE. */
export interface ConsoleQueryResult {
  id: string;
  document: string;
  /** Id of the parent document — lets the UI deep-link to Documenti. */
  document_id?: string;
  /** Origin of the parent document (connector type or "upload"). */
  source?: string;
  snippet: string;
  score: number;
}

export interface ConsoleQueryCitation {
  index: number;
  document: string;
  /** Id of the parent document (runtime/agentic wire: ``{document_id, chunk_id}``). */
  document_id?: string;
  /** Id of the cited chunk in ``results`` — lets citation chips jump to it. */
  chunk_id?: string;
  snippet?: string;
}

export interface ConsoleQueryResponse {
  mode: ConsoleQueryMode;
  answer: string | null;
  results: ConsoleQueryResult[];
  citations: ConsoleQueryCitation[];
}

// ── GET /sources (playground scope discovery) ─────────────────────────────
//
// Typed 1:1 against ``src/api/platform/v1/sources.py``. Heterogeneous BY
// CONTRACT: unstructured entries carry ``documents``/``chunks``, structured
// ones carry ``connector_type`` (WHITE-LABEL capability — never a vendor).
// Both always carry ``id``, ``kind``, ``label``, ``last_sync_at``,
// ``queryable`` and ``reason`` (``queryable:false`` always explains itself).

export type ConsoleSourceKind = "unstructured" | "structured";

/**
 * A source's discovery state (console-only). ``ready`` = TRAMA can query it
 * (a structured schema was discovered, or unstructured content is indexed);
 * ``pending`` = still in discovery. There is NO "published" state: the legacy
 * publish flag never gated retrieval, so it is not represented.
 */
export type ConsoleSourceState = "ready" | "pending";

export interface ConsoleSourceEntry {
  /** Selector you scope a request with (``drive`` / ``conn_<uuid>``). */
  id: string;
  kind: ConsoleSourceKind;
  /**
   * Display-safe, white-label label — never a vendor brand. For a STRUCTURED
   * source this is the CAPABILITY label (``Gestionale (SQL)`` …), which the
   * Schema map uses as its GROUPING dimension.
   */
  label: string;
  /**
   * Structured only: the admin's distinguishing display name (the alias they
   * set, or a derived default). Console-only — the public ``/v1/sources`` never
   * carries it. This is the LEAF label under the ``label`` group.
   */
  name?: string | null;
  /** Unstructured only: indexed document / chunk counts. */
  documents?: number | null;
  chunks?: number | null;
  /** Structured only: white-label capability (``database``/``spreadsheet``…). */
  connector_type?: string | null;
  /** Structured only: count of profiled objects (0 ⇒ still in discovery). */
  object_count?: number | null;
  /** Structured only: discover→publish lifecycle state. */
  state?: ConsoleSourceState | null;
  last_sync_at: string | null;
  queryable: boolean;
  /** Human explanation when ``queryable`` is false; null otherwise. */
  reason: string | null;
}

export interface ConsoleSourcesResponse {
  sources: ConsoleSourceEntry[];
  total: number;
  count: number;
  has_more: boolean;
  next_cursor: string | null;
}

/** Fresh id + resolved display name returned by ``PATCH /sources/{id}/name``. */
export interface ConsoleRenamedSource {
  id: string;
  name: string;
}

// ── UKS (real, structure-only facet forest) ────────────────────────────────
//
// Session projection of ``console/uks.py`` — TRAMA's ACTUAL materialized UKS,
// the non-structured folder/source tree it navigates. Structure-only + display
// safe: names, paths, counts, a content digest; never the profiling internals
// or the fusion/entity layer (the moat stays opaque).

/** One non-structured knowledge node (folder or source) of the real UKS. */
export interface ConsoleUksFacet {
  id: string;
  name: string;
  path: string;
  /** ``"source"`` (drive/email/…) or ``"folder"``. */
  level: "source" | "folder";
  docs: number;
  chunks: number;
  /** Short "what's in here" digest, or null. */
  digest: string | null;
  /** Top file types (e.g. ``["pdf","xlsx"]``). */
  doc_types: string[];
}

/** A containment edge (folder → subfolder) — the tree shape. */
export interface ConsoleUksEdge {
  src: string;
  dst: string;
}

export interface ConsoleUksResponse {
  /** False when the tenant's UKS has not been materialized yet. */
  materialized: boolean;
  profiled_at: string | null;
  facets: ConsoleUksFacet[];
  edges: ConsoleUksEdge[];
}

// ── K-Schema curation (GET/PATCH/POST /sources/{id}/schema) ────────────────
//
// Session projection of ``ando-agent`` ``console/schema.py`` — the human
// review + PUBLISH gate for a structured source's profiled K-Schema. Discovery
// + LLM profiling land AI-proposed table/column descriptions; an integrator
// CURATES them and PUBLISHES, at which point ``/query`` + ``/agentic`` may use
// the source. Unpublished ⇒ ``queryable=false, reason="schema non pubblicato"``.

/** The white-label status of a structured source, publish gate included. */
export interface ConsoleSchemaStatus {
  id: string;
  /** White-label capability (``database`` / ``spreadsheet`` …) — never a vendor. */
  connector_type: string;
  label: string;
  last_sync_at: string | null;
  published: boolean;
  published_at: string | null;
  queryable: boolean;
  /** Human explanation when ``queryable`` is false (e.g. "schema non pubblicato"). */
  reason: string | null;
}

/** One column of a profiled object: name + type + AI-proposed/curated description. */
export interface ConsoleSchemaColumn {
  name: string;
  data_type: string | null;
  description: string | null;
}

/** One profiled object (table/view) with its curated descriptions. */
export interface ConsoleSchemaObjectView {
  object: string;
  description: string;
  columns: ConsoleSchemaColumn[];
  semantic_mappings: Record<string, unknown>[];
  /** True once a human overrode the AI-proposed descriptions (survives re-profiling). */
  user_edited: boolean;
  updated_at: string | null;
}

/** ``GET /sources/{id}/schema`` — the source status + every profiled object. */
export interface ConsoleSchemaResponse {
  source: ConsoleSchemaStatus;
  objects: ConsoleSchemaObjectView[];
  count: number;
}

/** ``PATCH`` body — the human override; at least one field must be present. */
export interface ConsoleSchemaObjectCuration {
  description?: string;
  column_descriptions?: Record<string, string>;
  semantic_mappings?: Record<string, unknown>[];
}

// ── POST /agentic/answer (playground) ─────────────────────────────────────
//
// Typed against ``src/api/platform/v1/agentic.py`` (``AgenticRequest`` /
// ``AgenticAnswer``), plus the console-session ``as_key_id`` extension that
// simulates one AppKey's resource policy — the exact parallel to
// ``ConsoleQueryRequest`` over the runtime ``QueryRequest``.

/** Coarse source buckets — a bucket enables its half of the stack. */
export const CONSOLE_SOURCE_BUCKETS = ["documents", "structured", "web"] as const;
export type ConsoleSourceBucket = (typeof CONSOLE_SOURCE_BUCKETS)[number];

/** ``k`` / ``max_steps`` bounds — mirrored from the backend Field limits. */
export const CONSOLE_AGENTIC_K_MIN = 1;
export const CONSOLE_AGENTIC_K_MAX = 50;
export const CONSOLE_AGENTIC_STEPS_MIN = 1;
export const CONSOLE_AGENTIC_STEPS_MAX = 12;

export interface ConsoleAgenticRequest {
  /** Omit for the original infrastructure; explicit choice never grants access. */
  trama_infrastructure?: "current" | "lakehouse";
  query: string;
  k: number;
  /** Buckets + concrete ``GET /sources`` ids; omit for the default scope. */
  sources?: string[];
  max_steps: number;
  /** Continue a conversation from ``POST /conversations`` (multi-turn). */
  conversation_id?: string;
  with_diagnostics: boolean;
  include_rows: boolean;
  /** Console-session extension: preview one AppKey's resource policy. */
  as_key_id?: string;
}

/**
 * One audit-trail entry. OPEN shape by contract — every entry carries at
 * least ``step`` / ``type`` / ``status``; new types and keys are additive,
 * so the UI reads a curated white-label allowlist and ignores the rest.
 * The wire never carries vendor brands or internal engine names.
 */
export interface ConsoleAgenticStep {
  step: number;
  type: string;
  status: string;
  [key: string]: unknown;
}

export interface ConsoleAgenticResponse {
  answer: string | null;
  citations: ConsoleQueryCitation[];
  /** Buckets that actually contributed (``documents`` / ``structured`` / ``web``). */
  sources_used: string[];
  steps: ConsoleAgenticStep[];
  /** Always present (``path``/``tier``/``focus``); enriched with ``with_diagnostics``. */
  diagnostics: Record<string, unknown>;
  conversation_id: string | null;
  degraded: boolean;
  degraded_reason?: string | null;
  no_results: boolean;
}

// ── POST /conversations (multi-turn) ──────────────────────────────────────

export interface ConsoleConversation {
  conversation_id: string;
  title: string;
  message_count: number;
  created_at: string;
  last_message_at: string;
}

// ── Flows (console projection of the Hive Flows engine) ───────────────────
//
// Session-auth projection of the LIVE product flows engine
// (``ando-agent/src/api/platform/console/flows.py`` — thin delegation to the
// SAME ``FlowService`` the product ``/workspace/flows`` builder uses). The
// row / template / stats / run wire shapes are therefore the PRODUCT shapes
// already typed in ``@ando/core`` (``api/endpoints/hiveFlows.ts``) —
// re-exported under console naming instead of re-declared (DRY).
//
//   GET    /flows?page=&page_size=&status=   { flows, total }
//   GET    /flows/templates                  { templates }
//   GET    /flows/{id}                       Flow
//   GET    /flows/{id}/stats                 FlowStats
//   POST   /flows/{id}/run                   FlowRun — a HITL approval step
//                                            parks it as ``pending_approval``
//   POST   /flows                            201, the created Flow row
//   POST   /flows/generate                   AI co-pilot draft (loose shape,
//                                            see ConsoleFlowGenerateResult)
//   PATCH  /flows/{id}                       FlowUpdate → the updated Flow
//   DELETE /flows/{id}                       204

export type {
  Flow as ConsoleFlow,
  FlowCreatePayload as ConsoleFlowCreate,
  FlowGraph as ConsoleFlowGraph,
  FlowRun as ConsoleFlowRun,
  FlowRunRequest as ConsoleFlowRunRequest,
  FlowScheduleType as ConsoleFlowScheduleType,
  FlowStats as ConsoleFlowStats,
  FlowStatus as ConsoleFlowStatus,
  FlowTemplate as ConsoleFlowTemplate,
  FlowUpdatePayload as ConsoleFlowUpdate,
} from "@ando/core";

/**
 * ``POST /flows/generate`` — the AI co-pilot outcome, typed DEFENSIVELY.
 * The product copilot returns ``{graph, ok, errors}`` (a compiled draft
 * graph, nothing persisted); the console projection MAY instead persist a
 * draft and return the created flow — top-level or under ``flow``. Every
 * field is optional: callers feature-detect (see ``FlowsPage``) and fall
 * back to a generic success.
 */
export interface ConsoleFlowGenerateResult {
  ok?: boolean;
  errors?: Array<string | { message?: string | null }> | null;
  graph?: FlowGraph | null;
  flow?: Flow | null;
  flow_id?: string | null;
  name?: string | null;
}

/**
 * ``GET /flows`` — list envelope. The wire carries ``{flows, total}`` ONLY
 * (no page/page_size echo — the caller owns its pagination state).
 */
export interface ConsoleFlowsResponse {
  flows: Flow[];
  total: number;
}

/** ``GET /flows/templates`` — the built-in starter-template gallery. */
export interface ConsoleFlowTemplatesResponse {
  templates: FlowTemplate[];
}

// ── Flows builder surface (console-hosted editor, Aug 2026) ───────────────
//
// The console now hosts the FULL visual builder (palette + canvas +
// inspector + dry-run) against ``/platform/console/v1/flows/*`` — the same
// ``FlowService`` (and thus the same wire shapes, already typed in
// ``@ando/core``) as the product ``/api/v1/hive-flows/*``:
//
//   GET  /flows/palette                    PaletteResponse
//   GET  /flows/nodes/{type}/schema        NodeSchemaResponse
//   GET  /flows/pending-approvals          { approvals: PendingApproval[] }
//   POST /flows/runs/{run_id}/approve      { ok, status } — edited_value and/or
//                                          edited_fields ride along (G-2)
//   POST /flows/runs/{run_id}/reject       { ok, status } — terminal "no"
//   POST /flows/{id}/run-upload            FlowRun (multipart, PDF trigger)
//   GET  /flows/runs/{run_id}              FlowRunDetailResponse (polling)

export type {
  ApproveRunEdits as ConsoleApproveRunEdits,
  ConfigFieldSpec as ConsoleConfigFieldSpec,
  FlowRunDetailResponse as ConsoleFlowRunDetail,
  FlowRunListResponse as ConsoleFlowRunList,
  NodePaletteItem as ConsoleNodePaletteItem,
  NodeSchemaResponse as ConsoleNodeSchemaResponse,
  PaletteResponse as ConsoleFlowPaletteResponse,
  PendingApproval as ConsolePendingApproval,
} from "@ando/core";

/** ``GET /flows/pending-approvals`` — runs parked on a ``logic.approval`` node. */
export interface ConsolePendingApprovalsResponse {
  approvals: PendingApproval[];
}

/** ``POST /flows/runs/{run_id}/approve`` — approve (and resume) outcome. */
export interface ConsoleFlowRunApproveResponse {
  ok: boolean;
  status: string;
}

// ── Members / team (console session projection) ────────────────────────────
//
// Session projection of ``ando-agent/src/api/platform/console/members.py`` —
// the org membership + email-invite roster (owner/admin manage; only owner
// changes roles). ``role`` on a MEMBER is a free string on the wire (the BE
// types it ``str``); the WRITE surfaces (invite / change-role) accept only the
// three-role literal ``ConsoleMemberRole``.

/** The three assignable roles (``PATCH``/``POST`` write vocabulary). */
export type ConsoleMemberRole = "owner" | "admin" | "member";

/** Membership state of one roster row. */
export type ConsoleMemberStatus = "active" | "invited" | "suspended";

/** One teammate — display-safe identity + membership state. */
export interface ConsoleMember {
  member_id: string;
  email: string | null;
  display_name: string | null;
  role: string;
  status: ConsoleMemberStatus;
  last_active_at: string | null;
  joined_at: string | null;
}

/** One pending email invitation (``organization_invites`` row). */
export interface ConsoleInvite {
  invite_id: string;
  email: string;
  role: string;
  expires_at: string | null;
  invited_at: string | null;
  invited_by: string | null;
}

/** Seat consumption vs the org cap; ``total=null`` means unlimited. */
export interface ConsoleSeatUsage {
  used: number;
  total: number | null;
}

/** ``GET /members`` — roster, pending invites, and seat usage. */
export interface ConsoleMembersResponse {
  members: ConsoleMember[];
  invites: ConsoleInvite[];
  seats: ConsoleSeatUsage;
}

/** One assignable role with plain-language console copy. */
export interface ConsoleRoleInfo {
  id: string;
  label: string;
  description: string;
}

/** ``GET /members/roles`` — the assignable role catalog, most-privileged first. */
export interface ConsoleRolesResponse {
  roles: ConsoleRoleInfo[];
}

/**
 * ``POST /members/invites`` / ``.../resend`` outcome — the org-service
 * dispatcher shape. Exactly one of ``invite`` / ``member`` is populated per
 * ``action`` (``invited`` for a new email, ``added`` / ``reactivated`` for an
 * existing user, ``migration_required`` when the user belongs to another org).
 */
export interface ConsoleInviteResult {
  action: string;
  invite?: Record<string, unknown> | null;
  member?: Record<string, unknown> | null;
  current_org?: Record<string, unknown> | null;
}

/** ``DELETE /members/invites/{invite_id}``. */
export interface ConsoleInviteRevoked {
  revoked: boolean;
  invite_id: string;
}

/** ``DELETE /members/{member_id}``. */
export interface ConsoleMemberRemoved {
  removed: boolean;
  member_id: string;
}

// ── Quotas (rate-limit + spend-cap visibility) ─────────────────────────────
//
// Session projection of ``ando-agent/src/api/platform/console/quotas.py`` —
// read-only. The top-level envelope is ``{ keys, count }`` (NOT ``quotas``).
// ``current_usage`` / ``remaining`` are ``null`` when the rate-limiter's Redis
// is unavailable (fail-soft); ``monthly_limit_cents`` / ``remaining_cents`` are
// ``null`` for an uncapped key. Spend figures are RAW pre-markup cents.

/** A key's live rate-limit posture in the sliding window. */
export interface ConsoleQuotaRateLimit {
  limit: number;
  window_seconds: number;
  /** Live in-window request count; ``null`` when Redis is unavailable. */
  current_usage: number | null;
  /** Headroom (``limit - current_usage``); ``null`` when Redis is unavailable. */
  remaining: number | null;
}

/** A key's monthly spend-cap posture (RAW pre-markup cents). */
export interface ConsoleQuotaSpendCap {
  /** The per-key monthly cap; ``null`` = uncapped. */
  monthly_limit_cents: number | null;
  month_to_date_cents: number;
  /** Cents before the cap trips; ``null`` when uncapped. */
  remaining_cents: number | null;
}

/** One AppKey's rate-limit + spend-cap posture. */
export interface ConsoleQuota {
  key_id: string;
  name: string;
  rate_limit_tier: string;
  rate_limit: ConsoleQuotaRateLimit;
  spend_cap: ConsoleQuotaSpendCap;
}

/** ``GET /quotas`` — every AppKey of the project with its budgets. */
export interface ConsoleQuotasResponse {
  keys: ConsoleQuota[];
  count: number;
}

// ── Key rotation + in-place edit ───────────────────────────────────────────
//
// Session projection of ``POST /keys/{key_id}/roll`` and ``PATCH /keys/{id}``
// (``ando-agent/src/api/platform/console/keys.py``).

/**
 * ``POST /keys/{key_id}/roll`` — the NEW key's one-time secret (same shape as
 * create) plus the rotated (old) key's grace state. ``old_key_expires_at`` is
 * the old key's new expiry (``now + grace_hours``); it is ``null`` with
 * ``old_key_revoked=true`` when ``grace_hours=0`` revoked it outright.
 */
export interface ConsoleKeyRolled extends ConsoleCreateKeyResponse {
  resource_policy?: ConsoleResourcePolicy | null;
  old_key_id: string;
  old_key_expires_at: string | null;
  old_key_revoked: boolean;
}

/**
 * ``PATCH /keys/{key_id}`` body — partial edit of scopes and/or policy. Both
 * optional; provide at least one. An omitted field is left untouched (true
 * PATCH semantics), so scopes and the resource policy edit independently.
 */
export interface ConsoleKeyUpdateRequest {
  scopes?: string[];
  resource_policy?: ConsoleResourcePolicy | null;
}

// ── Per-key usage (billing view — carries cost) ────────────────────────────
//
// Session projection of ``GET /keys/{key_id}/usage`` and ``GET /usage/by-key``
// (``ando-agent/src/api/platform/console/data.py``). UNLIKE ``/usage/breakdown``
// (counts-only), the per-key surface IS a billing view and carries the
// versioned, marked-up ``cost_usd`` on every row and total.

/** Window sums for a key — the counts-only totals plus marked-up cost. */
export interface ConsoleKeyUsageTotals extends ConsoleUsageWindowTotals {
  /** Versioned, marked-up LLM cost over the window, in USD. */
  cost_usd: number;
}

/** One zero-filled day of a key's requests + tokens + marked-up cost trend. */
export interface ConsoleKeyUsageDay extends ConsoleUsageTrendDay {
  cost_usd: number;
}

/** A consuming model for one key (top consumers), with marked-up cost. */
export interface ConsoleKeyUsageModelEntry extends ConsoleUsageModelEntry {
  cost_usd: number;
}

/** A call-purpose bucket for one key, with marked-up cost. */
export interface ConsoleKeyUsageSourceEntry extends ConsoleUsageSourceEntry {
  cost_usd: number;
}

/** ``GET /keys/{key_id}/usage?window=`` — one key's consumption breakdown. */
export interface ConsoleKeyUsage {
  window: ConsoleUsageWindow;
  key_id: string;
  days: ConsoleKeyUsageDay[];
  totals: ConsoleKeyUsageTotals;
  by_model: ConsoleKeyUsageModelEntry[];
  by_source: ConsoleKeyUsageSourceEntry[];
}

/** One AppKey ranked by consumption; ``name`` is ``null`` for a deleted key. */
export interface ConsoleUsageByKeyEntry {
  key_id: string;
  name: string | null;
  requests: number;
  tokens: number;
  cost_usd: number;
}

/** ``GET /usage/by-key?window=`` — the project's keys ranked by consumption. */
export interface ConsoleUsageByKey {
  window: ConsoleUsageWindow;
  by_key: ConsoleUsageByKeyEntry[];
  totals: ConsoleKeyUsageTotals;
}

// ── Webhook secret rotation ────────────────────────────────────────────────

/**
 * ``POST /webhooks/{webhook_id}/rotate-secret`` — the NEW ``whsec_`` signing
 * secret, shown EXACTLY ONCE (same one-time-reveal contract, and same fields,
 * as registration — hence the alias to ``ConsoleWebhookCreated``).
 */
export type ConsoleWebhookSecretRotated = ConsoleWebhookCreated;

/** Read-only lossless pages of text already acquired for one document. */
export interface ConsoleAcquiredPart {
  chunk_id: string;
  chunk_index: number | null;
  section_name: string | null;
  chunk_type: string | null;
  source: string;
  text: string;
  text_offset: number;
  text_length: number;
}
export interface ConsoleAcquiredContent {
  document_id: string;
  parts: ConsoleAcquiredPart[];
  total: number;
  chunk_count: number;
  limit: number;
  offset: number;
}

/** An organization credential shared by its configurable inference purposes. */
export interface ConsoleProviderAccount {
  provider: string;
  auth_method: "api_key" | "delegated_identity";
  source?: "provider" | "legacy" | "none";
  legacy_purposes?: string[];
  credential_status: "saved" | "none" | "error";
  byok_masked?: string | null;
  byok_last4?: string | null;
  params: Record<string, unknown>;
  updated_at?: string | null;
  delegation_approved?: boolean;
}

export type ConsoleIngestionSelection = { folder_ids: string[]; file_ids: string[] } | { chat_ids: string[] } | ConsolePersonalCalendarSelection;
export interface ConsoleIngestionEstimateDraft { selection: ConsoleIngestionSelection | null; settings: ConsolePersonalSyncSettings | null; }
export interface ConsoleEstimateAmount { low: string; expected: string | null; high: string; }
export interface ConsoleIngestionEstimate {
  estimate_id: string;
  status: "ready" | "partial" | "unavailable";
  work_mode: "initial" | "incremental" | "scope_rebuild";
  currency: "USD";
  customer_amount: ConsoleEstimateAmount | null;
  known_subtotal: ConsoleEstimateAmount | null;
  quantity: { observed_items: number; unknown_items: number | null; enumeration_complete: boolean; approximate?: boolean };
  coverage: { method: string; uncovered_stages: string[] };
  selection_hash: string; scope_version: string; pricing_version: string;
  pipeline_version: string; estimator_version: string; expires_at: string;
  assumptions: string[]; warnings: string[];
}
